import { config } from '@/services/configService';
import CustomerHelper from '@/helpers/customerHelper';
import { getAuthToken } from '@/services/authToken';

export type CrudContentType = 'json' | 'form' | 'multipart';
export type CrudResponseType = 'json' | 'text' | 'blob';

export type CrudOptions = {
  baseUrl?: string;
  allowAuth?: boolean;
  handleUnauthorized?: boolean;
  headers?: Record<string, string>;
  responseType?: CrudResponseType;
  // Cliente del CRM: 'cz' indica que la sesión va en la cookie de cliente (mk_auth_cz). naavi
  // pasa null para no mandarlo (otro origen; evita preflight CORS innecesario).
  audience?: string | null;
  // Devuelve { ok, status, data } sin lanzar ni redirigir, para cuando hace falta el código
  // de estado (p.ej. distinguir un 409). Sin raw, las llamadas devuelven solo el body.
  raw?: boolean;
};

export type CrudRawResult<T = unknown> = {
  ok: boolean;
  status: number;
  data: T | null;
};

type CrudCallback = (ok: boolean, payload?: unknown) => unknown;

export class CrudService {
  baseUrl: string;
  allowAuth: boolean;
  handleUnauthorized: boolean;
  audience: string | null;

  constructor(options: CrudOptions = {}) {
    this.baseUrl = options.baseUrl ? String(options.baseUrl) : config.apiUrl;
    this.allowAuth = options.allowAuth !== false;
    this.handleUnauthorized = options.handleUnauthorized !== false;
    this.audience = options.audience !== undefined ? options.audience : 'cz';
  }

  async httpGet(
    url: string,
    body?: unknown,
    callback?: CrudCallback,
    contentType: CrudContentType = 'json',
    options: CrudOptions = {},
  ) {
    return this.httpFetch(url, 'GET', body, callback, contentType, options);
  }

  async httpPost(
    url: string,
    body?: unknown,
    callback?: CrudCallback,
    contentType: CrudContentType = 'json',
    options: CrudOptions = {},
  ) {
    return this.httpFetch(url, 'POST', body, callback, contentType, options);
  }

  async httpPut(
    url: string,
    body?: unknown,
    callback?: CrudCallback,
    contentType: CrudContentType = 'json',
    options: CrudOptions = {},
  ) {
    return this.httpFetch(url, 'PUT', body, callback, contentType, options);
  }

  async httpDelete(
    url: string,
    body?: unknown,
    callback?: CrudCallback,
    contentType: CrudContentType = 'json',
    options: CrudOptions = {},
  ) {
    return this.httpFetch(url, 'DELETE', body, callback, contentType, options);
  }

  async httpFetch(
    url: string,
    method: string,
    body?: unknown,
    callback?: CrudCallback,
    contentType: CrudContentType = 'json',
    options: CrudOptions = {},
  ) {
    let response: Response | null = null;
    try {
      const requestOptions = this.buildRequest(method, contentType, body, options);
      const requestUrl = this.buildUrl(url, options.baseUrl);
      response = await fetch(requestUrl, requestOptions);

      if (options.raw) {
        const data = await this.readResponse(response, options.responseType);
        return { ok: response.ok, status: response.status, data };
      }

      if (!response.ok) {
        return await this.handleBadRequest(response, callback, options);
      }

      return await this.handleResponse(response, callback, options);
    } catch (error) {
      if (callback) {
        callback(false);
      }
      return options.raw ? { ok: false, status: 0, data: null } : null;
    }
  }

  private buildUrl(url: string, overrideBase?: string) {
    if (!url) {
      return this.baseUrl;
    }
    if (this.isAbsoluteUrl(url)) {
      return url;
    }
    const base = overrideBase ? String(overrideBase) : this.baseUrl;
    const normalizedBase = this.normalizeBase(base);
    if (!normalizedBase) {
      return url;
    }
    const baseHasSlash = normalizedBase.endsWith('/');
    const urlHasSlash = url.startsWith('/');
    if (baseHasSlash && urlHasSlash) {
      return normalizedBase + url.slice(1);
    }
    if (!baseHasSlash && !urlHasSlash) {
      return `${normalizedBase}/${url}`;
    }
    return normalizedBase + url;
  }

  private isAbsoluteUrl(url: string) {
    return url.startsWith('http://')
      || url.startsWith('https://')
      || url.startsWith('//');
  }

  private normalizeBase(base: string) {
    if (!base) {
      return '';
    }
    if (this.isAbsoluteUrl(base) || base.startsWith('/')) {
      return base;
    }
    return `//${base}`;
  }

  private buildRequest(
    method: string,
    contentType: CrudContentType,
    body?: unknown,
    options: CrudOptions = {},
  ) {
    const headers = new Headers();
    const mergedHeaders: Record<string, string> = {
      Accept: 'application/json',
      ...(this.audience ? { 'X-Auth-Audience': this.audience } : {}),
      ...(options.headers || {}),
    };

    if (contentType !== 'multipart') {
      const hasContentType = Object.keys(mergedHeaders)
        .some((key) => key.toLowerCase() === 'content-type');
      if (!hasContentType) {
        mergedHeaders['Content-Type'] = this.getContentType(contentType);
      }
    }

    if (this.shouldAttachAuth(options)) {
      const token = this.resolveToken();
      if (token) {
        mergedHeaders.Authorization = `Bearer ${token}`;
      }
    }

    Object.keys(mergedHeaders).forEach((key) => {
      headers.append(key, mergedHeaders[key]);
    });

    return {
      method,
      headers,
      // Envía la cookie de sesión HttpOnly (same-site api.medkus.com). En naavi (otro origen)
      // no se manda la cookie host-only, por eso allí seguimos adjuntando el bearer en memoria.
      credentials: 'include' as RequestCredentials,
      body: this.buildBody(contentType, body),
    };
  }

  private shouldAttachAuth(options: CrudOptions) {
    if (options.allowAuth === false) {
      return false;
    }
    return this.allowAuth;
  }

  private resolveToken() {
    // Token en memoria (no localStorage). La sesión va por cookie HttpOnly; el bearer solo
    // hace falta para naavi (customer-bookings).
    return getAuthToken();
  }

  private async handleResponse(response: Response, callback?: CrudCallback, options: CrudOptions = {}) {
    const data = await this.readResponse(response, options.responseType);
    if (callback) {
      if (data && typeof data === 'object' && 'errors' in data) {
        return callback(response.ok, (data as { errors?: unknown }).errors);
      }
      return callback(response.ok, data);
    }
    return data;
  }

  private async handleBadRequest(response: Response, callback?: CrudCallback, options: CrudOptions = {}) {
    if (response.status === 401 || response.status === 403) {
      const shouldHandle = options.handleUnauthorized !== false && this.handleUnauthorized;
      if (shouldHandle) {
        this.handleUnauthorizedResponse();
        return null;
      }
    }
    return await this.handleResponse(response, callback, options);
  }

  private handleUnauthorizedResponse() {
    CustomerHelper.logout({ redirect: false });
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  private buildBody(contentType: CrudContentType, body?: unknown) {
    if (body === undefined || body === null) {
      return null;
    }
    if (contentType === 'json') {
      return JSON.stringify(body);
    }
    return body as BodyInit;
  }

  private getContentType(contentType: CrudContentType) {
    if (contentType === 'json') {
      return 'application/json-patch+json';
    }
    if (contentType === 'form') {
      return 'application/x-www-form-urlencoded';
    }
    if (contentType === 'multipart') {
      return 'multipart/form-data';
    }
    return 'application/json';
  }

  private async readResponse(response: Response, responseType?: CrudResponseType) {
    if (responseType === 'blob') {
      return await response.blob();
    }
    if (responseType === 'text') {
      return await response.text();
    }
    const responseCType = response.headers.get('content-type');
    if (responseCType && (
      responseCType.includes('application/json')
      || responseCType.includes('application/problem+json')
    )) {
      try {
        return await response.json();
      } catch (error) {
        return null;
      }
    }
    try {
      return await response.text();
    } catch (error) {
      return null;
    }
  }
}
